<template>
  <div
    class="AppDetailView container-md">

    <header
      class="AppDetailView_header">
      <BaseButton
        class="AppDetailView_BackBtn"
        size="small"
        theme="ghost"
        :to="computedBackRoute">
        ❮ APPSTORE
      </BaseButton>

      <transition
        name="fade">
        <BaseLoader
          class="_loader"
          v-show="!detailData"
        />
      </transition>
    </header>

    <div
      class="_layout">
      <div
        class="_leading">

        <DetailSummary
          :app="appData"
          :loading="loadingDetailData"
        />

        <div
          class="mt-4"
          ref="AppDetailViewOverview">
          <BaseTabs
            v-if="computedOverviewTabs.length"
            key="ADV-overview-tabs-mobile"
            :value="$route.params.tab"
            :tabs="computedOverviewTabs"
            @input="handleTabChange"
          />

          <BasePlaceholder
            v-if="!detailData"
            class="my-2 ml-1"
            width="80px"
            height="24px"
          />

          <div
            class="bg-white border p-3">
            <BasePlaceholder
              v-if="!detailData"
              class="mb-2"
              width="80%"
              height="300px"
            />
            <div
              v-if="$route.params.tab === 'overview' || !$route.params.tab"
              key="ADV-DetailOverview">

              <DetailImageGallery
                v-if="computedDetailImages.length"
                class="AppDetailView_DetailImageGallery"
                :images="computedDetailImages"
                key="ADV-DetailImageGallery"
              />

              <div
                class="AppDetailView_Overview"
                v-html="computedOverviewText">
              </div>
            </div>

            <DetailActionAndMeta
              v-if="$route.params.tab === 'details' && isMobile"
              :app="appData"
              :loading="loadingDetailData"
              key="ADV-DetailActionAndMeta-Mobile"
              @see-datasets-click="scrollToOverview"
            />

            <DetailDataSets
              v-if="$route.params.tab === 'datasets' && appData.dataSets"
              :data-sets="appData.dataSets"
              key="ADV-DetailDataSets"
            />

            <DetailFaqs
              v-if="$route.params.tab === 'faq' && appData.faqs"
              :faqs="appData.faqs"
              key="ADV-DetailFaqs"
            />
          </div>
        </div>

        <DetailPromos
          class="AppDetailView_Promos"
          v-if="isMobile"
          key="ADV-Promos"
        />

        <DetailEula
          class="AppDetailView_EULA"
        />
      </div>

      <div
        v-if="!isMobile"
        class="_trailing"
        key="ADV-DetailActionAndMeta-Desktop">
        <DetailActionAndMeta
          class="DetailActionAndMeta-Desktop"
          :app="appData"
          :loading="loadingDetailData"
          @see-datasets-click="scrollToOverview"
        />

        <DetailPromos
          class="AppDetailView_Promos"
        />
      </div>
    </div>

  </div>
</template>

<script>
import BaseLoader from '@/components/BaseLoader.vue';
import BasePlaceholder from '@/components/BasePlaceholder.vue';
import BaseButton from '@/components/BaseButton.vue';
import DetailFaqs from '@/components/DetailFaqs.vue';
import DetailDataSets from '@/components/DetailDataSets.vue';
import BaseTabs from '@/components/BaseTabs.vue';
import DetailSummary from '@/components/DetailSummary.vue';
import DetailImageGallery from '@/components/DetailImageGallery.vue';
import DetailActionAndMeta from '@/components/DetailActionAndMeta.vue';
import DetailPromos from '@/components/DetailPromos.vue';
import DetailEula from '@/components/DetailEula.vue';

const TAB_FIELD_MAP = [
  {
    fieldName: 'description',
    labelName: 'overview',
    needsValueToDisplay: true,
    mobileOnly: false,
  },
  {
    fieldName: 'details',
    labelName: 'details',
    needsValueToDisplay: false,
    mobileOnly: true,
  },
  {
    fieldName: 'dataSets',
    labelName: 'datasets',
    needsValueToDisplay: true,
    mobileOnly: false,
  },
  {
    fieldName: 'faqs',
    labelName: 'faq',
    needsValueToDisplay: true,
    mobileOnly: false,
  },
];

export default {
  name: 'AppDetailView',

  components: {
    BaseLoader,
    BasePlaceholder,
    BaseButton,
    DetailFaqs,
    DetailDataSets,
    BaseTabs,
    DetailSummary,
    DetailImageGallery,
    DetailActionAndMeta,
    DetailPromos,
    DetailEula,
  },

  metaInfo() {
    return this.generateMeta(this.appData);
  },

  props: {
    optimisticDetailData: { type: Object, default: null },

    loadingDetailData: { type: Boolean, default: true },

    detailViewBackRoute: { type: Object, default: null },

    detailData: { type: Object, default: null },
    appType:    { type: String, required: true },
    appName:    { type: String, required: true },
    tab:        { type: String, default: 'overview' },
  },

  computed: {
    appData() {
      return this.detailData || this.optimisticDetailData || {};
    },

    computedBackRoute() {
      return this.detailViewBackRoute || { name: 'home' };
    },

    computedOverviewText() {
      if (!this.appData.description) return '';
      if (typeof this.appData.description === 'object') return this.appData.description.join('<br>');
      return this.appData.description;
    },

    isMobile() {
      return this.$root.window.width < 800;
    },

    computedOverviewTabs() {
      const tabs = [];

      if (!this.detailData) return tabs;

      TAB_FIELD_MAP.forEach((f) => {
        if (f.mobileOnly && !this.isMobile) return;
        if (f.needsValueToDisplay && !this.appData[f.fieldName]) return;
        tabs.push({
          label: f.labelName.toUpperCase(),
          value: f.labelName.toLowerCase(),
        });
      });

      return tabs;
    },

    computedDetailImages() {
      const images = this.appData.images
        ? this.appData.images
          .filter((i) => i.type === 'screenshot')
          .map((image) => ({ src: image.path, thumbnail: image.path, type: 'image' }))
        : [];

      const videos = this.appData.videoID
        ? [this.appData.videoID]
          .map((video) => ({ src: video.videoId, thumbnail: video.thumbnail, type: 'video' }))
        : [];

      return [...videos, ...images];
    },
  },

  watch: {
    isMobile(nowMobile, wasMobile) {
      if (wasMobile && this.$route.params.tab === 'details') {
        this.$router.replace({
          name: 'app-detail',
          params: {
            appType: this.$route.params.appType || 'app',
            appName: this.$route.params.appName || 'null',
            tab: 'overview',
          },
        });
      }
    },
  },

  methods: {
    handleTabChange(tab) {
      if (this.$route.params.tab === tab) return;

      this.$router.replace({
        name: 'app-detail',
        params: {
          appType: this.$route.params.appType || 'app',
          appName: this.$route.params.appName || 'null',
          tab,
        },
      });
    },

    scrollToOverview() {
      const overviewTopPos = this.$refs.AppDetailViewOverview.offsetTop;

      return window.scrollTo({
        left: 0,
        top: overviewTopPos - 80,
        behavior: 'smooth',
      });
    },

    generateMeta(appData) {
      const seoHasProp = (prop) => !!appData.seo && appData.seo[prop];

      const seo = {
        title: seoHasProp('title') ? appData.seo.title : appData.title,
        description: seoHasProp('description') ? appData.seo.description : null,
        noindex: seoHasProp('noindex'),
        canonical: seoHasProp('canonicalUrl'),
      };

      const description = this.generateMetaDescription(appData.priceType, seo);
      const robots = { vmid: 'robots', name: 'robots', content: 'noindex' };
      const canonical = { vmid: 'canonical', rel: 'canonical', href: seo.canonical };

      const meta = seo.noindex
        ? [description, robots]
        : [description];

      const link = seo.canonical
        ? [canonical]
        : null;

      return {
        title: seo.title,
        meta,
        link,
      };
    },

    generateMetaDescription(priceType, { title, description }) {
      const defaultDescription = priceType === 'negotiable'
        ? `The ${title} provides real-time data integration with powerful visualizations to answer critical business questions. Get more value from your data now.`
        : `Domo’s ${title} allows users to quickly integrate, transform, analyze, and visualize data with ease. Get more value from your data now.`;

      const content = description || defaultDescription;

      return {
        vmid: 'description',
        name: 'description',
        content,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/_variables';

.AppDetailView {
  padding: 24px 32px 80px 32px;
}

.AppDetailView_header {
  ._loader {
    display: inline-block;
    vertical-align: middle;
  }
}

.AppDetailView_BackBtn {
  margin-left: -12px;
  margin-bottom: 16px;
  color: $dark-blue;
}

.AppDetailView ._layout {
  display: flex;
}

._layout ._leading {
  flex: 1 1 auto;
}

._layout ._trailing {
  flex: 0 1 330px;
  min-width: 330px;
  padding-left: 24px;
}

.AppDetailView_Overview {
  white-space: pre-wrap;
}

.AppDetailView_DetailImageGallery + .AppDetailView_Overview {
  margin-top: 32px;
}

.DetailActionAndMeta-Desktop {
  max-width: 330px;
  padding: 24px;
  border: $border;
  background-color: $white;
}

.AppDetailView_Promos {
  padding: 24px;
  margin-top: 24px;
  border: $border;
  background-color: $white;
}

@media only screen and (max-width: 600px) {
  .AppDetailView {
    padding: 0;
  }

  .AppDetailView_header {
    display: none;
  }

  ._layout ._leading {
    padding-right: 0;
  }

  .AppDetailView_EULA {
    padding: 0 24px 48px 24px;
  }
}
</style>
